import { AppImages, useAppI18N, variantProvider, React } from '@/app'
import { Image, Text, View } from '@/components'
import { modal, ModalParams } from '@codeleap/modals'
import { motion } from 'framer-motion'
import { useAnimatedStyle, useIsMobile } from '@/utils'
import { CompleteLessonBadge } from '../atoms'
import { LessonCTAWrapper } from '../organisms'
import { Learn } from '@/features'

type ModalLessonDoneProps = {
  origin: Learn.T.LessonOrigin
  name?: string
  type?: 'lesson' | 'module'
}

const Content = (props: ModalParams<ModalLessonDoneProps>) => {
  const chapter = Learn.S.lesson.use(s => s.chapter)
  const module = Learn.S.lesson.use(s => s.module)
  const isMobile = useIsMobile()

  const { t } = useAppI18N()

  const { toggle, visible, origin, type = 'lesson', name = chapter?.title } = props

  const chapterType = chapter?.primary_cta?.type
  const IMAGE_WIDTH = (() => {
    if (['prompt', 'contribute'].includes(chapterType)) {
      return isMobile ? 280 : 280
    }
    return isMobile ? 440 : 280
  })()

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: visible ? 1 : 0,
      scale: visible ? 1 : 0,
      transition: {
        duration: 0.5,
      },
    }
  }, [visible])

  const animatedTextStyle = useAnimatedStyle(() => {
    return {
      opacity: visible ? 1 : 0,
      transition: {
        duration: 0.7,
      },
    }
  }, [visible])

  return (
    <View style={styles.wrapper}>
      <View style={styles.content}>
        {/* @ts-ignore */}
        <motion.div {...animatedTextStyle}>
          <Text variants={['h1', 'color:neutral10', 'textCenter']} text={t('learn.components.doneModal.title')} />
          <Text
            variants={['p1', 'color:neutral10', 'textCenter', 'paddingHorizontal:1']}
            text={`${t('learn.components.doneModal.subtitle')}  ‘${name}‘!`}
          />
        </motion.div>

        {/* @ts-ignore */}
        <motion.div {...animatedStyle} style={styles.imageWrapper}>
          <Image
            source={type === 'lesson' ? AppImages.celebration2 : AppImages.trophy}
            objectFit='cover'
            style={{ width: IMAGE_WIDTH }}
          />
        </motion.div>
      </View>

      <View variants={['fullWidth', 'gap:2', 'column', 'fullHeight', 'justifyEnd', 'marginBottom:4']}>
        <CompleteLessonBadge />
        <LessonCTAWrapper origin={origin} module={module} chapter={chapter} toggle={toggle} type={type} />
      </View>
    </View>
  )
}

const MODAL_WIDTH_DEFAULT = 480
const MODAL_WIDTH_MOBILE = 600

const styles = variantProvider.createComponentStyle(
  theme => ({
    wrapper: {
      maxHeight: '90svh',
      ...theme.presets.column,
      overflowY: 'scroll',

      [theme.media.down('small')]: {
        minHeight: '100dvh',
        maxHeight: '100dvh',
        width: '100vw',
        overflowY: 'hidden',
      },
    },
    box: {
      padding: 0,
      ...theme.spacing.marginVertical('auto'),
      backgroundColor: theme.colors.primary1,

      [theme.media.down('small')]: {
        ...theme.spacing.marginVertical(0),
        width: '100vw',
        maxWidth: MODAL_WIDTH_MOBILE,
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
      },
    },
    body: {
      backgroundColor: theme.colors.primary1,
      width: '100vw',
      maxWidth: MODAL_WIDTH_DEFAULT,

      [theme.media.down('small')]: {
        height: '100dvh',
        maxWidth: '100vw',
        maxHeight: '100dvh',
      },
    },
    content: {
      flexDirection: 'column-reverse',
      ...theme.spacing.marginBottom(1),
      ...theme.presets.justifyCenter,
      ...theme.presets.fullWidth,

      [theme.media.down('small')]: {
        flexDirection: 'column',
        position: 'absolute',
        ...theme.spacing.marginTop(4),
      },
    },
    imageWrapper: {
      width: '100%',
      display: 'flex',
      ...theme.presets.center,
      ...theme.presets.relative,

      [theme.media.down('small')]: {
        ...theme.spacing.marginTop(0),
      },
    },
  }),
  true,
)

export const ModalLessonDone = modal<ModalLessonDoneProps>()
  .content(p => <Content {...p} />)
  .props({
    variants: ['centered'],
    showClose: false,
    styles: {
      body: styles.body,
      box: styles.box,
      innerWrapper: {
        overflowY: 'hidden',
      },
    },
  })
